import { reducerSwitch, createReducer } from 'lib/reducerUtils'

import { FETCH_REVIEWS, FETCH_REVIEWS_ORDER, UPSERT_REVIEW, DELETE_REVIEW, UPLOAD_REVIEW_PHOTO } from 'actions/apiActionConstants'

import {
  API_CALL_REQUEST,
  API_CALL_FAILURE,
  API_CALL_SUCCESS,
  USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE,
  USER_REVIEW_SET_UPSERTED,
} from 'actions/actionConstants'

const initialState: App.ReviewState = {
  reviews: [],
  reviewRating: null,
  isReviewUpserted: false,
  isReviewDeleted: false,
  totalReviews: null,
  fetchingReviews: false,
  fetchingReviewCreate: false,
  reviewCreateError: null,
  reviewDeleteError: null,
  reviewsByOrder: {},
  reviewsByOrderLoading: {},
  reviewPhotosUpload: {},
}

const apiRequests = reducerSwitch<App.ReviewState>({
  [FETCH_REVIEWS]: () => ({
    fetchingReviews: true,
  }),
  [FETCH_REVIEWS_ORDER]: (state, action) => ({
    isReviewDeleted: false,
    reviewsByOrderLoading: {
      ...state.reviewsByOrderLoading,
      [action.orderId]: true,
    },
  }),
  [UPSERT_REVIEW]: () => ({
    fetchingReviewCreate: true,
    isReviewUpserted: false,
    reviewCreateError: null,
    isReviewDeleted: false,
  }),
  [DELETE_REVIEW]: (state, action) => ({
    isReviewDeleted: false,
    reviewDeleteError: null,
    reviewsByOrderLoading: {
      ...state.reviewsByOrderLoading,
      [action.orderId]: true,
    },
  }),
  [UPLOAD_REVIEW_PHOTO]: (state, action) => ({
    reviewPhotosUpload: {
      ...state.reviewPhotosUpload,
      [action.uploadId]: {
        isUploading: true,
        error: undefined,
      },
    },
  }),
})

const apiSuccesses = reducerSwitch<App.ReviewState>({
  [FETCH_REVIEWS]: (_, action) => {
    return {
      fetchingReviews: false,
      reviews: action.data.reviews,
      totalReviews: action.data.total,
    } },
  [FETCH_REVIEWS_ORDER]: (state, action) => ({
    reviewsByOrder: {
      ...state.reviewsByOrder,
      [action.orderId]: action.data.reviews,
    },
    reviewsByOrderLoading: {
      ...state.reviewsByOrderLoading,
      [action.orderId]: false,
    },
    reviewRating: null,
  }),
  [UPSERT_REVIEW]: (state, action) => ({
    reviewCreateError: null,
    fetchingReviewCreate: false,
    isReviewUpserted: action.data.userName !== 'Anonymous', // When form autosubmit, it save userName as "Anonymous"
    reviewRating: action.data.rating,
    reviewsByOrder: {
      ...state.reviewsByOrder,
      [action.data.orderId]: [action.data], // TODO improve when there's more then one review per order
    },
  }),
  [DELETE_REVIEW]: (state, action) => {
    const newReviewsByOrder = { ...state.reviewsByOrder }
    const newReviewsByOrderLoading = { ...state.reviewsByOrderLoading }

    delete newReviewsByOrder[action.orderId]
    delete newReviewsByOrderLoading[action.orderId]

    return {
      reviewsByOrder: newReviewsByOrder,
      reviewsByOrderLoading: newReviewsByOrderLoading,
      isReviewDeleted: true,
    }
  },
  [UPLOAD_REVIEW_PHOTO]: (state, action) => ({
    reviewPhotosUpload: {
      ...state.reviewPhotosUpload,
      [action.uploadId]: {
        ...state.reviewPhotosUpload[action.uploadId],
        isUploading: false,
        imageId: action.data.imageId,
      },
    },
  }),
})

const apiFailures = reducerSwitch<App.ReviewState>({
  [FETCH_REVIEWS]: () => ({
    fetchingReviews: false,
  }),
  [FETCH_REVIEWS_ORDER]: (state, action) => ({
    reviewsByOrderLoading: {
      ...state.reviewsByOrderLoading,
      [action.orderId]: false,
    },
  }),
  [UPSERT_REVIEW]: (_, action) => ({
    fetchingReviewCreate: false,
    isReviewUpserted: false,
    reviewCreateError: action.error,
  }),
  [DELETE_REVIEW]: (state, action) => ({
    isReviewDeleted: false,
    reviewDeleteError: action.error,
    reviewsByOrderLoading: {
      ...state.reviewsByOrderLoading,
      [action.orderId]: false,
    },
  }),
  [UPLOAD_REVIEW_PHOTO]: (state, action) => {
    return ({
      reviewPhotosUpload: {
        ...state.reviewPhotosUpload,
        [action.uploadId]: {
          ...state.reviewPhotosUpload[action.uploadId],
          isUploading: false,
          error: action.error,
        },
      },
    })
  },
})

export default createReducer<App.ReviewState>(initialState, {
  [API_CALL_REQUEST]: (state, action) => apiRequests(action.api)(state, action),
  [API_CALL_FAILURE]: (state, action) => apiFailures(action.api)(state, action),
  [API_CALL_SUCCESS]: (state, action) => apiSuccesses(action.api)(state, action),
  [USER_REVIEW_SET_PHOTO_UPLOAD_PERCENTAGE]: (state, action) => ({
    reviewPhotosUpload: {
      ...state.reviewPhotosUpload,
      [action.uploadId]: {
        ...state.reviewPhotosUpload[action.uploadId],
        percentage: action.percentage,
      },
    },
  }),
  [USER_REVIEW_SET_UPSERTED]: (_, action) => ({
    isReviewUpserted: action.isReviewUpserted,
  }),
})
